'use client'

import { create } from 'zustand'

import type { ChatItem } from '@/features/veluvit/lib/chat'
import type { PendingRequest } from '@/features/veluvit/lib/chat-request'
import type { Operation } from '@/features/veluvit/services/api'
import type { PresetId, Segment } from '@/features/veluvit/types/api.types'

/**
 * 대화방의 화면 전용 상태 — 레퍼런스 S.chats[id] 중 서버에 없는 값.
 * (오프닝 재생 위치, 응답 대기, 방금 보낸 말(낙관적 표시), 오류 줄, 추천 사용 여부, 유료 요청 진행)
 */
type PaidLoading = { kind: 'snaps' | 'suggestions'; since: number } | null

export type NextSettings = { roomId: string; model?: string; preset?: PresetId }

type ChatUi = {
  roomId: string | null
  /** 새 방이면 오프닝을 한 줄씩 재생한다 — 끝나면 true */
  opened: boolean
  openIdx: number
  busy: boolean
  /** 서버 응답 전까지 보여 주는 줄(내가 보낸 말·오류·궁합 결과) */
  local: ChatItem[]
  /** 오프닝이 끝난 직후 덧붙는 안내 줄 — 서버 방을 다시 받으면 사라진다 */
  intro: ChatItem[]
  sugsUsed: boolean
  loading: PaidLoading
  /** 장면 선택으로 들어왔을 때 오프닝 뒤에 알릴 장소 */
  sceneNote: string | null
  /** 이번에 새로 만든 방 — 첫 응답을 받기 전까지 오프닝을 화면에서 재생한다 */
  fresh: boolean
  /** 보내지 못한 문장 — 입력창이 한 번 가져가면 비운다 */
  restoreDraft: string | null
  restoreDraftChannel: 'dialogue' | 'ooc' | null
  pendingOperation: Operation | null
  lastOperation: Operation | null
  tentativeSegments: Segment[]
  tentativePhase: string | null
  tentativeCursor: string | null
  requestSnapshot: PendingRequest | null
  requestError: { message: string; retryable: boolean; code?: string } | null
  nextSettings: NextSettings | null
}

const EMPTY: ChatUi = {
  roomId: null,
  opened: true,
  openIdx: 0,
  busy: false,
  local: [],
  intro: [],
  sugsUsed: false,
  loading: null,
  sceneNote: null,
  fresh: false,
  restoreDraft: null,
  restoreDraftChannel: null,
  pendingOperation: null,
  lastOperation: null,
  tentativeSegments: [],
  tentativePhase: null,
  tentativeCursor: null,
  requestSnapshot: null,
  requestError: null,
  nextSettings: null,
}

type ChatStore = {
  chats: Record<string, ChatUi>
  get: (workId: string) => ChatUi
  patch: (workId: string, patch: Partial<ChatUi>) => void
  reset: (workId: string) => void
}

export const useChatStore = create<ChatStore>()((set, get) => ({
  chats: {},
  get: (workId) => get().chats[workId] || EMPTY,
  patch: (workId, patch) =>
    set({
      chats: {
        ...get().chats,
        [workId]: { ...(get().chats[workId] || EMPTY), ...patch },
      },
    }),
  reset: (workId) => {
    const next = { ...get().chats }
    delete next[workId]
    set({ chats: next })
  },
}))

export function useChatUi(workId: string): ChatUi {
  return useChatStore((s) => s.chats[workId] || EMPTY)
}

/* ---------- 입력 초안 · 보내던 요청 — 새로고침해도 남긴다 (레퍼런스 localStorage draft/pending) ---------- */
function key(userId: string | undefined, name: string) {
  return `veluvit_v2_original:${userId || 'visitor'}:${name}`
}

export function readLocal<T>(
  userId: string | undefined,
  name: string,
  fallback: T
): T {
  try {
    const raw = localStorage.getItem(key(userId, name))
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

export function writeLocal(
  userId: string | undefined,
  name: string,
  value: unknown
) {
  try {
    if (value === null || value === '')
      localStorage.removeItem(key(userId, name))
    else localStorage.setItem(key(userId, name), JSON.stringify(value))
  } catch {
    // 저장소를 쓸 수 없으면 초안 복구만 포기한다
  }
}
